import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

function SortProductsModal({ products, onReorder, isOpen, onClose }) {
  const [orderedProducts, setOrderedProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    if (isOpen && products) {
      // Convert [sku, product] tuples to product objects
      const productObjs = products.map(([sku, product]) => ({
        ...product,
        sku: sku
      }));
      setOrderedProducts(productObjs.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    }
  }, [isOpen, products]);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newOrder = [...orderedProducts];
    const draggedItem = newOrder[draggedIndex];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(index, 0, draggedItem);
    
    setOrderedProducts(newOrder);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSave = async () => {
    setSaving(true);
    // Create payload with sku and sort_order for each product
    const productOrders = orderedProducts.map((product, index) => ({
      sku: product.sku,
      sort_order: index
    }));
    await onReorder(productOrders);
    setSaving(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Sort Products</DialogTitle>
          <DialogDescription>
            Drag and drop products to reorder them. The order will affect how they appear on the frontend.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 overflow-y-auto py-4 flex-1">
          {orderedProducts.map((product, index) => (
            <div
              key={product.sku}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-3 p-3 border rounded cursor-move transition-all ${
                draggedIndex === index ? 'opacity-50 bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="font-medium">{product.name || product.size}</div>
                <div className="text-sm text-gray-600">{product.sku}</div>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-custom-orange hover:bg-custom-orange">
            {saving ? 'Saving...' : 'Save Order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SortProductsModal;
